/** @jsxImportSource react */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { parseClawAgentSessionKey, useClawWs } from '@src/hooks/useClawWs';
import useClawWsStore from '@src/stores/useClawWsStore';
import type { ClawSession } from '@src/stores/useClawWsStore';
import {
  Global01NavNavmoreLineIcon,
  Icon,
  modalConfirm,
  PinLineIcon,
  UnpinLineIcon,
  Input,
  ModalWithMask,
} from '@kim/ui';
import intl from '@/internationalzation/intl';
import { Dropdown } from '@src/components/pc/sidebar/components/dropdown/DropDown';
import Tooltips from 'rc-tooltip';
import './SessionsPopover.less';
import {
  getBaseKeyModule,
  getClawSessionUrlBaseKey,
} from '@src/hooks/useClawWs';
import { useNavigate, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { useDebugMode } from '@/hooks/useDebugMode';
import useAgentsStore from '@/stores/useAgentsStore';
import {
  deleteMarioAgentThreadMessages,
  updateMarioAgentThreadTitle,
} from '@/services/community';
import { message } from 'antd';
import { isMarioWebchatSessionKey } from '@/stores/useClawWsStore';
import {
  buildMainagentPath,
  MAINAGENT_ROUTE_BASE,
} from '@src/utils/mainagent-route';

const RenameModal = ModalWithMask as any;

// ─── 时间分组 ─────────────────────────────────────────────────────────────────

const MS_7D = 7 * 24 * 60 * 60 * 1000;
const MS_30D = 30 * 24 * 60 * 60 * 1000;

/** 后端可能给秒级或毫秒级时间戳，统一成毫秒（与 Date.now() 一致） */
function toEpochMs(raw: number | undefined): number {
  if (raw == null || raw <= 0) return 0;
  return raw < 1e12 ? raw * 1000 : raw;
}

interface SessionGroup {
  label: string;
  sessions: ClawSession[];
}

function groupSessions(sessions: ClawSession[]): SessionGroup[] {
  const now = Date.now();
  const g7: ClawSession[] = [];
  const g30: ClawSession[] = [];
  const gOld: ClawSession[] = [];

  for (const s of sessions) {
    const ts = toEpochMs(s.updatedAt ?? s.createdAt);
    const age = now - ts;
    if (age <= MS_7D) g7.push(s);
    else if (age <= MS_30D) g30.push(s);
    else gOld.push(s);
  }

  const groups: SessionGroup[] = [];
  if (g7.length) groups.push({ label: intl.t('近7天'), sessions: g7 });
  if (g30.length) groups.push({ label: intl.t('近30天'), sessions: g30 });
  if (gOld.length) groups.push({ label: intl.t('过去'), sessions: gOld });
  return groups;
}

// ─── 会话行 ───────────────────────────────────────────────────────────────────

interface SessionRowProps {
  session: ClawSession;
  active: boolean;
  hasUnread: boolean;
  onClick: () => void;
  onRename: (key: string, label: string) => void;
  onDelete: (key: string) => void;
  currentSessionKey: string | null;
}

const SessionRow: React.FC<SessionRowProps> = ({
  session,
  active,
  hasUnread,
  onClick,
  onRename,
  onDelete,
  currentSessionKey
}) => {
  const displayLabel =
    session.label ??
    session.displayName ??
    session.display_name ??
    session.key ??
    '未命名会话';

  const [hover, setHover] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const infoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (active && currentSessionKey !== session.key) {
      onClick();
    }
  }, [currentSessionKey, session.key, active])

  const startEdit = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      onRename(session.key, displayLabel);
      setHover(false);
    },
    [displayLabel],
  );

  const handleDelete = useCallback(() => {
    modalConfirm({
      content: intl.t('mario.delete.confirm'),
      okText: intl.t('mario.button.delete'),
      cancelText: intl.t('mario.button.cancel'),
      confirmBtnType: 'danger',
      onOK: () => {
        onDelete(session.key);
      },
    });
  }, [session.key, onDelete]);

  const dropList = useMemo(
    () => [
      {
        id: 'rename',
        iconNode: (
          <Icon
            name="eed_web_icon_global_04_textaction_bianji_line"
            size={16}
          />
        ),
        text: intl.t('mario.button.edit'),
        command: () => startEdit(),
      },
      {
        id: 'delete',
        iconNode: (
          <Icon
            name="eed_web_icon_global_04_textaction_shanchu_line"
            size={16}
          />
        ),
        text: intl.t('mario.button.delete'),
        command: handleDelete,
      },
    ],
    [handleDelete, startEdit],
  );

  useEffect(() => {
    if (!infoRef.current) return;
    const { width } = infoRef.current.getBoundingClientRect();
    const tempTitle = document.createElement('div');
    tempTitle.className = 'sessions-popover__item-text';
    tempTitle.textContent = displayLabel;
    tempTitle.style.position = 'absolute';
    tempTitle.style.zIndex = '0';
    tempTitle.style.whiteSpace = 'nowrap';
    infoRef.current.appendChild(tempTitle);
    const { width: tempWidth } = tempTitle.getBoundingClientRect();
    infoRef.current.removeChild(tempTitle);
    setShowTooltip(tempWidth > width);
  }, [displayLabel]);

  return (
    <div
      className={`sessions-popover__item${active ? ' sessions-popover__item--active' : ''}`}
      onClick={onClick}
      onMouseEnter={() => {
        setHover(true);
      }}
      onMouseLeave={() => {
        setHover(false);
      }}
      onDoubleClick={startEdit}
    >
      <>
        <div className="sessions-popover__item-info" ref={infoRef}>
          {showTooltip ? (
            <Tooltips
              overlayClassName="sessions-popover__item-text-tooltip"
              overlay={displayLabel}
              placement="top"
              mouseEnterDelay={0.2}
              overlayStyle={{ maxWidth: 248 }}
            >
              <span className="sessions-popover__item-text">
                {displayLabel}
              </span>
            </Tooltips>
          ) : (
            <span className="sessions-popover__item-text">{displayLabel}</span>
          )}
        </div>
        {hover && (
          <div
            className="sessions-popover__item-actions"
            onClick={(e) => e.stopPropagation()}
          >
            <Dropdown
              dropList={dropList}
              placement="bottom-start"
              trigger="click"
              offset={[0, 14]}
              className="kim-ai-mario-session-dropdown"
              onVisibleChange={(visible) => {
                if (!visible) {
                  setHover(false);
                }
              }}
            >
              <Global01NavNavmoreLineIcon size={14} className="edit-icon" />
            </Dropdown>
          </div>
        )}
        {!active && hasUnread && (
          <span className="sessions-popover__item-unread-dot" />
        )}
      </>
    </div>
  );
};

// ─── SessionsPopover ──────────────────────────────────────────────────────────

interface SessionsPopoverProps {
  open: boolean;
  anchorRef?: React.RefObject<HTMLElement | null>;
  onClose?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  /** 固定面板模式：始终展示，占位右侧，无浮动关闭 */
  variant?: 'dropdown' | 'panel';
  /** 嵌入 PC 侧边栏历史区：无标题/固定按钮，样式由外层容器约束 */
  embedInSidebar?: boolean;
}

const SessionsPopover: React.FC<SessionsPopoverProps> = ({
  open,
  anchorRef: _anchorRef,
  onClose,
  onMouseEnter,
  onMouseLeave,
  variant = 'dropdown',
  embedInSidebar = false,
}) => {
  const { fetchSessions, switchSession, renameSession, deleteSession } =
    useClawWs();
  const navigate = useNavigate();
  const location = useLocation();
  const urlSessionBase = getClawSessionUrlBaseKey(location.pathname);
  const {
    sessions,
    sessionsPopoverPinned,
    setSessionsPopoverPinned,
    currentSessionKey,
    setCurrentSessionKey,
    fetchMarioAgentThreadsAsClawSessions,
    marioThreadsLoading,
  } = useClawWsStore();
  const { agentList } = useAgentsStore();
  const { setSessions } = useClawWsStore();
  const { unreadSessionKeys } = useClawWsStore((s) => ({
    unreadSessionKeys: s.unreadSessionKeys,
    setSessions: s.setSessions,
  }));
  const popoverRef = useRef<HTMLDivElement>(null);
  const renameInfoRef = useRef<{ key: string; name: string }>({
    key: '',
    name: '',
  });
  const [renameModalVisible, setRenameModalVisible] = useState(false);
  const [renameInputValue, setRenameInputValue] = useState('');

  const isPanel = variant === 'panel';

  useEffect(() => {
    if (!sessions.length || !sessions) {
      // 再 fetch 一次
      setTimeout(() => {
        fetchSessions().catch(() => {});
      }, 1000);
    }
  }, [sessions]);

  useEffect(() => {
    if (
      !marioThreadsLoading &&
      sessions.filter((s) => isMarioWebchatSessionKey(s.key)).length === 0
    ) {
      fetchMarioAgentThreadsAsClawSessions().catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (open || isPanel || embedInSidebar) {
      fetchSessions().catch(() => {});
    }
  }, [open, isPanel, embedInSidebar, fetchSessions]);

  const handleSessionClick = useCallback(
    (key: string) => {
      switchSession(key);
      navigate(buildMainagentPath(getBaseKeyModule(key)));
      if (!isPanel && !embedInSidebar && onClose) onClose();
    },
    [switchSession, navigate, isPanel, embedInSidebar, onClose],
  );

  const handlePinToggle = useCallback(() => {
    setSessionsPopoverPinned(!sessionsPopoverPinned);
  }, [sessionsPopoverPinned, setSessionsPopoverPinned]);

  const handleRenameStart = useCallback((key: string, name: string) => {
    if (!key) return;
    renameInfoRef.current = { key, name };
    setRenameInputValue(name);
    setRenameModalVisible(true);
  }, []);

  const handleRenameOk = useCallback(async () => {
    const value = renameInputValue.trim();
    if (!value || value === renameInfoRef.current.name) {
      setRenameModalVisible(false);
      return;
    }
    const { agentId, sessionBaseKey } = parseClawAgentSessionKey(
      renameInfoRef.current.key,
    );
    const agent = agentList.find((item) => item.marioAgentId === agentId);
    if (agent?.agentType === 'mario-agent') {
      const res = await updateMarioAgentThreadTitle(
        {},
        {
          marioAgentId: agent?.marioAgentId,
          threadId: sessionBaseKey,
          title: value,
        },
      );
      if (res.code !== 0) return message.error(res.message);
      const newSessions = useClawWsStore
        .getState()
        .sessions.map((s) =>
          s.key === renameInfoRef.current.key ? { ...s, label: value } : s,
        );
      setSessions(newSessions);
    } else {
      await renameSession(renameInfoRef.current.key, value);
    }
    setRenameModalVisible(false);
  }, [renameInputValue, renameSession, agentList, setSessions]);

  const handleDelete = useCallback(
    async (key: string) => {
      const { agentId, sessionBaseKey } = parseClawAgentSessionKey(key);
      const agent = agentList.find((item) => item.marioAgentId === agentId);
      if (agent?.agentType === 'mario-agent') {
        const res = await deleteMarioAgentThreadMessages(
          {},
          {
            marioAgentId: agent?.marioAgentId,
            threadId: sessionBaseKey,
          },
        );
        if (res.code !== 0) return message.error(res.message);
        const newSessions = useClawWsStore
          .getState()
          .sessions.filter((s) => s.key !== key);
        setSessions(newSessions);
        if (currentSessionKey === key) {
          setCurrentSessionKey(null);
          navigate(MAINAGENT_ROUTE_BASE);
        }
      } else {
        deleteSession(key);
      }
    },
    [agentList, currentSessionKey, deleteSession],
  );

  const handleRenameCancel = useCallback(() => {
    setRenameModalVisible(false);
    renameInfoRef.current = { key: '', name: '' };
  }, []);

  const { isDebugMode } = useDebugMode();

  const preparedSessions = useMemo(
    () =>
      sessions.filter((s) => {
        const { sessionBaseKey } = parseClawAgentSessionKey(s.key);
        return (
          isDebugMode ||
          (!sessionBaseKey?.startsWith('kim-group-') &&
            !sessionBaseKey?.startsWith('kim-bot-'))
        );
      }),
    [sessions, isDebugMode],
  );

  const groups = useMemo(
    () => groupSessions(preparedSessions),
    [preparedSessions],
  );

  if (!open && !isPanel && !embedInSidebar) return null;

  return (
    <div
      className={`sessions-popover${isPanel ? ' sessions-popover--panel' : ''}${embedInSidebar ? ' sessions-popover--sidebar-embed' : ''}`}
      ref={popoverRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={(e) => e.stopPropagation()}
    >
      {/* 标题 + Pin（侧栏嵌入模式不展示） */}
      {!embedInSidebar && (
        <div className="sessions-popover__header">
          <span className="sessions-popover__title">会话列表</span>
          <button
            type="button"
            className="sessions-popover__pin"
            onClick={handlePinToggle}
            title={sessionsPopoverPinned ? '取消固定' : '固定到右侧'}
          >
            {!sessionsPopoverPinned ? (
              <PinLineIcon size={16} />
            ) : (
              <UnpinLineIcon size={16} />
            )}
          </button>
        </div>
      )}

      {/* 内容 */}
      <div className="sessions-popover__body">
        {groups.length === 0 ? (
          <div className="sessions-popover__empty">暂无任务</div>
        ) : (
          groups.map((group) => (
            <React.Fragment key={group.label}>
              <div className="sessions-popover__group">
                <span className="sessions-popover__group-label">
                  {group.label}
                </span>
              </div>
              {group.sessions.map((s) => (
                <SessionRow
                  key={s.key}
                  session={s}
                  active={
                    urlSessionBase !== null &&
                    getBaseKeyModule(s.key) === urlSessionBase
                  }
                  currentSessionKey={currentSessionKey}
                  hasUnread={unreadSessionKeys.includes(s.key)}
                  onClick={() => handleSessionClick(s.key)}
                  onRename={handleRenameStart}
                  onDelete={handleDelete}
                />
              ))}
            </React.Fragment>
          ))
        )}
      </div>
      {renameModalVisible && (
        <RenameModal
          visible={true}
          className={classNames('mario-rename-modal', {
            disabled: !renameInputValue.trim(),
          })}
          title={intl.t('mario.button.rename')}
          okText={intl.t('Sure')}
          cancelText={intl.t('mario.button.cancel')}
          onOK={handleRenameOk}
          onCancel={handleRenameCancel}
        >
          <Input
            autoFocus={true}
            value={renameInputValue}
            onChange={(e: any) => {
              setRenameInputValue(e?.target?.value ?? '');
            }}
            type="text"
            maxLength={50}
            className="mario-rename-input"
          />
        </RenameModal>
      )}
    </div>
  );
};

export default SessionsPopover;
